import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, UserCog } from "lucide-react";
import { PageHeader, ExportBar } from "@/components/EntityPage";

const ROLES = ["admin", "subadmin", "operator", "accounts", "sales", "driver", "vendor", "client"];
const SECTIONS = [
  "dashboard","vehicles","drivers","driver-admin","vendors","clients","trips","vendor-accounting",
  "calendar","leads","reviews","invoices","lottery","users","settings",
];

function readFile(file) {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

function UserForm({ initial, onSubmit }) {
  const [f, setF] = useState(initial || { role: "operator", sections: [] });
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const toggleSection = (s) => {
    const cur = new Set(f.sections || []);
    if (cur.has(s)) cur.delete(s); else cur.add(s);
    setF({ ...f, sections: [...cur] });
  };
  async function pick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800_000) return toast.error("Image too large (max ~800KB)");
    const dataUrl = await readFile(file);
    setF({ ...f, photo_url: dataUrl });
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-slate-700 overflow-hidden grid place-items-center">
          {f.photo_url ? <img src={f.photo_url} alt="" className="w-full h-full object-cover"/> : <UserCog className="w-6 h-6 text-slate-400"/>}
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Profile photo</Label>
          <Input type="file" accept="image/*" onChange={pick} data-testid="user-photo" className="mt-1 bg-[hsl(224_40%_12%)]"/>
          <div className="text-[10px] text-muted-foreground mt-1">Max image size: <span className="text-amber-300">800 KB</span> · JPG/PNG/WEBP</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[10px] uppercase text-muted-foreground">Name</Label>
          <Input required value={f.name || ""} onChange={set("name")} data-testid="user-name" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Email</Label>
          <Input required type="email" value={f.email || ""} onChange={set("email")} disabled={!!initial} data-testid="user-email" className="bg-[hsl(224_40%_12%)] mt-1 disabled:opacity-70"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Role</Label>
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="user-role" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Phone</Label>
          <Input value={f.phone || ""} onChange={set("phone")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div className="col-span-2"><Label className="text-[10px] uppercase text-muted-foreground">{initial ? "New password (leave blank to keep)" : "Initial password"}</Label>
          <Input type="password" value={f.password || ""} onChange={set("password")} data-testid="user-password" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
      </div>
      <div>
        <Label className="text-[10px] uppercase text-muted-foreground">Section access (leave empty for role default)</Label>
        <div className="mt-2 grid grid-cols-3 gap-2 p-3 rounded-md border border-border bg-[hsl(224_40%_11%)]">
          {SECTIONS.map((s) => {
            const on = (f.sections || []).includes(s);
            return (
              <label key={s} data-testid={`sec-${s}`} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs capitalize border transition-colors ${on ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-border text-slate-300 hover:border-slate-500"}`}>
                <Checkbox checked={on} onCheckedChange={() => toggleSection(s)} className="border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:text-black h-4 w-4"/>
                {s}
              </label>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end"><Button type="submit" data-testid="user-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">{initial ? "Update user" : "Create user"}</Button></div>
    </form>
  );
}

export default function Users() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("staff"); // "staff" | "customers"
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() { const { data } = await api.get("/users"); setRows(data); }
  useEffect(() => { load(); }, []);

  async function save(f) {
    try {
      if (editing) {
        const upd = { ...f }; if (!upd.password) delete upd.password; delete upd.email;
        await api.put(`/users/${editing.id}`, upd);
      } else {
        await api.post("/users", f);
      }
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  async function del(id) {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }

  const STAFF_ROLES = ["admin", "subadmin", "operator", "accounts", "sales", "driver", "vendor"];
  const scoped = rows.filter((r) => tab === "customers" ? r.role === "client" : STAFF_ROLES.includes(r.role));
  const filtered = scoped.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
  const staffCount = rows.filter((r) => STAFF_ROLES.includes(r.role)).length;
  const customerCount = rows.filter((r) => r.role === "client").length;

  const roleColor = { admin: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    subadmin: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    operator: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    accounts: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    sales: "bg-pink-500/15 text-pink-300 border-pink-500/30",
    driver: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    vendor: "bg-pink-500/15 text-pink-300 border-pink-500/30",
    client: "bg-slate-500/15 text-slate-300 border-slate-500/30" };

  return (
    <div>
      <PageHeader title="Users & Roles" subtitle="Manage staff accounts and customer logins in two separate lists."
        right={<>
          <ExportBar rows={filtered.map((u) => ({ name: u.name, email: u.email, role: u.role, phone: u.phone }))} filename={tab === "customers" ? "customers" : "staff"} whatsappText={`${tab === "customers" ? "Customers" : "Staff"}: ${filtered.length}`} />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="add-user-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><Plus className="w-4 h-4"/> New {tab === "customers" ? "customer" : "staff member"}</Button></DialogTrigger>
            <DialogContent className="bg-[hsl(224_45%_9%)] border-border">
              <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit" : "New"} {tab === "customers" ? "customer" : "user"}</DialogTitle></DialogHeader>
              <UserForm initial={editing || (tab === "customers" ? { role: "client", sections: [] } : { role: "operator", sections: [] })} onSubmit={save}/>
            </DialogContent>
          </Dialog>
        </>}
      />

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4">
        <button data-testid="tab-staff" onClick={() => { setTab("staff"); setQ(""); }}
          className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${tab === "staff" ? "border-amber-500/60 bg-amber-500/15 text-amber-200" : "border-border bg-[hsl(224_45%_10%)] text-slate-300 hover:border-slate-500"}`}>
          Staff <span className="ml-1 text-xs opacity-70">({staffCount})</span>
        </button>
        <button data-testid="tab-customers" onClick={() => { setTab("customers"); setQ(""); }}
          className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${tab === "customers" ? "border-sky-500/60 bg-sky-500/15 text-sky-200" : "border-border bg-[hsl(224_45%_10%)] text-slate-300 hover:border-slate-500"}`}>
          Customers <span className="ml-1 text-xs opacity-70">({customerCount})</span>
        </button>
      </div>

      <Card className="bg-[hsl(224_45%_9%)] border-border">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <Search className="w-4 h-4 text-muted-foreground"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Filter ${tab === "customers" ? "customers" : "staff"}…`} data-testid="user-filter" className="bg-transparent border-0 focus-visible:ring-0 h-8"/>
          <div className="text-xs text-muted-foreground">{filtered.length} of {scoped.length}</div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
            {["","Name","Email","Role","Phone","Created","Actions"].map((h,i) => <th key={i} className="text-left p-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No {tab === "customers" ? "customers" : "staff members"} yet</td></tr>
            ) : filtered.map((u) => (
              <tr key={u.id} className="border-t border-border">
                <td className="p-3">{u.photo_url ? <img src={u.photo_url} alt="" className="w-9 h-9 rounded-full object-cover"/> : <div className="w-9 h-9 rounded-full bg-slate-700 grid place-items-center text-xs">{u.name?.[0]}</div>}</td>
                <td className="p-3">{u.name}</td>
                <td className="p-3 font-mono text-xs">{u.email}</td>
                <td className="p-3"><Badge className={roleColor[u.role]}>{u.role}</Badge></td>
                <td className="p-3 font-mono text-xs">{u.phone || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground">{u.created_at?.slice(0,10)}</td>
                <td className="p-3">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="icon" data-testid={`edit-user-${u.id}`} className="h-8 w-8" onClick={() => { setEditing(u); setOpen(true); }}><Pencil className="w-3.5 h-3.5"/></Button>
                    <Button variant="ghost" size="icon" data-testid={`del-user-${u.id}`} className="h-8 w-8 hover:text-red-400" onClick={() => del(u.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
